// NEW COMPONENT - Comprehensive FAQ Database for HMIS Smart Assistant
// This is a completely new addition and does not modify any existing UI

export const faqDatabase = [
  // ============ USER MANAGEMENT ============
  {
    id: 1,
    question: "How do I create a new user?",
    answer: "User creation is restricted to Admin and Super Admin roles only. Navigate to User Management from the sidebar, click 'New User' button, fill in all required fields including personal details, contact information, role assignment, and login credentials. The system will automatically send login credentials via email to the user.",
    category: "user_management", 
    keywords: ["create", "user", "new", "add", "staff", "employee", "admin", "management"],
    priority: 8
  },
  {
    id: 2,
    question: "How do I add a doctor to the system?",
    answer: "Doctors are added through the regular user creation process. Go to User Management, click 'New User', fill in all details, and specifically select 'Doctor' as the role. Additionally, you need to provide Registration Number, License Number, and set up their shift schedules.",
    category: "user_management",
    keywords: ["doctor", "add", "create", "medical", "physician", "staff"],
    priority: 7
  },
  {
    id: 3,
    question: "How do I change a user's role?",
    answer: "Only Admin and Super Admin can change user roles. Go to User Management, find the user in the list, click the **three dots menu** next to their name, and select **'Change Role'**. Choose the new role from the dropdown and save changes.",
    category: "user_management",
    keywords: ["role", "change", "assign", "permission", "access", "user"],
    priority: 6
  },

  // ============ PATIENT MANAGEMENT ============
  {
    id: 4,
    question: "How do I register a new patient?",
    answer: "Navigate to Patient Management from the sidebar, click 'Register New Patient' button. Fill in all mandatory fields including personal details, contact information, address, and emergency contact. The system will automatically generate a unique UHID (Unique Health ID) for the patient.",
    category: "patient_management",
    keywords: ["patient", "register", "new", "add", "registration", "UHID"],
    priority: 9
  },
  {
    id: 5,
    question: "How do I search for a patient?",
    answer: "Use the search bar in Patient Management with multiple search options: UHID, Name, Mobile Number, or Email. You can also apply date filters and status filters to narrow down results. The search supports partial matching for names.",
    category: "patient_management",
    keywords: ["search", "find", "patient", "UHID", "name", "mobile"],
    priority: 8
  },
  {
    id: 6,
    question: "How do I update patient information?",
    answer: "Go to Patient Management, find the patient in the list, click the **three dots menu** next to the patient name, and select **'Edit Details'**. This opens the patient form where you can update all information including personal details, contact info, and address.",
    category: "patient_management",
    keywords: ["update", "edit", "patient", "details", "information", "modify"],
    priority: 7
  },

  // ============ ADD SERVICE TO PATIENT (CORRECTED) ============
  {
    id: 7,
    question: "How do I add services to a patient?",
    answer: "There are **two ways** to add services to a patient in HMIS:\n\n**Method 1: From Patient Management (New Service Consultation)**\n1. Go to Patient Management from the sidebar\n2. Find the patient in the list\n3. Click the **three dots menu** next to the patient name\n4. Select **'Add Service'** from the dropdown menu\n5. This opens the Service Details form where you can add medical services\n\n**Method 2: From Existing Patient Visit (Add Items)**\n1. Go to Patient Info → Visits tab\n2. Select an existing consultation/visit\n3. In the visit details, click **'+ Add Service'** button\n4. Select services from categories: Consultation, Pathology, Radiology, or Pharmacy\n5. Fill in service details and save\n\n💡 **Note:** Services can include consultations, lab tests, radiology procedures, and pharmacy items.",
    category: "patient_management",
    keywords: ["add", "service", "patient", "consultation", "medical", "treatment", "procedure"],
    priority: 10
  },

  // ============ APPOINTMENTS ============
  {
    id: 8,
    question: "How do I create a new appointment?",
    answer: "Navigate to Appointments from the sidebar, click 'Book New Appointment' button. Select the patient (search by UHID or name), choose doctor, department, date and time slot. Add appointment type and any special notes. The system will send confirmation notifications.",
    category: "appointments",
    keywords: ["appointment", "book", "schedule", "create", "new", "doctor"],
    priority: 9
  },
  {
    id: 9,
    question: "How do I book an appointment for a patient?",
    answer: "You can book appointments in **two ways**:\n\n**Method 1: From Appointments Module**\n• Go to Appointments → Book New Appointment\n• Search and select patient\n• Choose doctor, date, and time\n\n**Method 2: From Patient Management**\n• Go to Patient Management\n• Find the patient, click **three dots menu**\n• Select **'Add Appointment'**\n• Choose doctor and schedule details",
    category: "appointments",
    keywords: ["book", "appointment", "patient", "schedule", "doctor", "time"],
    priority: 8
  },
  {
    id: 10,
    question: "How do I cancel or reschedule an appointment?",
    answer: "Go to Appointments, find the appointment in the list, click the **three dots menu** next to the appointment, and select either **'Cancel'** or **'Reschedule'**. For rescheduling, choose new date and time. The system will automatically notify the patient and doctor about the changes.",
    category: "appointments",
    keywords: ["cancel", "reschedule", "appointment", "change", "modify"],
    priority: 7
  },

  // ============ CONSULTATION & OPD ============
  {
    id: 11,
    question: "How do I create an OPD consultation?",
    answer: "There are **two methods** to create OPD consultation:\n\n**Method 1: From Patient List**\n1. Go to Patient Management\n2. Find the patient in the list\n3. Click the **three dots menu** next to patient name\n4. Select **'Add Consultation'**\n\n**Method 2: From Patient Details Page**\n1. Open the patient's profile/details page\n2. Click the **'Add Consultation'** button (located next to 'Add Appointment' button)\n3. Fill in consultation details, symptoms, diagnosis, and treatment plan\n4. Add prescriptions if needed and save the consultation",
    category: "opd_consultation",
    keywords: ["OPD", "consultation", "create", "start", "patient", "doctor"],
    priority: 9
  },
  {
    id: 12,
    question: "How do I start a consultation?",
    answer: "Navigate to the consultation from Patient Management or Appointments. Click on the consultation entry to open the consultation interface. Here you can record patient history, symptoms, examination findings, diagnosis, and create prescriptions.",
    category: "opd_consultation",
    keywords: ["start", "consultation", "begin", "record", "examination"],
    priority: 8
  },
  {
    id: 13,
    question: "How do I add prescriptions during consultation?",
    answer: "During an active consultation, go to the 'Prescription' tab. Click 'Add Medicine', search for medications from the database, specify dosage, frequency, duration, and special instructions. You can add multiple medications and generate a printable prescription.",
    category: "opd_consultation",
    keywords: ["prescription", "medicine", "add", "consultation", "dosage"],
    priority: 7
  },

  // ============ BILLING ============
  {
    id: 14,
    question: "How do I generate a bill for a patient?",
    answer: "Go to Patient Management, find the patient, click the **three dots menu**, and select **'Bill'**. This opens the billing module where you can add services, consultations, medicines, and procedures. The system automatically calculates totals including taxes and generates the final bill.",
    category: "billing",
    keywords: ["bill", "generate", "invoice", "payment", "charges"],
    priority: 8
  },
  {
    id: 15,
    question: "How do I create a bill?",
    answer: "Bills can be created from multiple modules:\n• **Patient Management**: Three dots menu → 'Bill'\n• **Appointments**: Three dots menu → 'Bill'\n• **Nursing**: Three dots menu → 'Bill'\n• **Pharmacy**: Three dots menu → 'Bill'\n\nSelect services, add consultation charges, include medications, and the system will calculate the total amount with applicable taxes.",
    category: "billing",
    keywords: ["create", "bill", "invoice", "charges", "payment"],
    priority: 7
  },
  {
    id: 16,
    question: "How do I process payments?",
    answer: "In the billing module, after generating the bill, select payment method (Cash, Card, UPI, Insurance). Enter payment details, apply discounts if applicable, and process the payment. The system will generate payment receipts and update patient accounts.",
    category: "billing",
    keywords: ["payment", "process", "cash", "card", "insurance", "receipt"],
    priority: 6
  },

  // ============ PHARMACY & INVENTORY ============
  {
    id: 17,
    question: "How do I manage pharmacy stock?",
    answer: "Navigate to Store Management from the sidebar. Here you can view current stock levels, add new medicines, update quantities, set expiry dates, and manage suppliers. Use the **three dots menu** options: **'View Details'**, **'Add Stock'**, or **'Delete'** for stock management.",
    category: "pharmacy_inventory",
    keywords: ["pharmacy", "stock", "medicine", "inventory", "manage"],
    priority: 7
  },
  {
    id: 18,
    question: "How do I add new medicines to inventory?",
    answer: "Go to Store Management, click 'Add New Stock' button. Fill in medicine details including name, batch number, expiry date, quantity, supplier information, and pricing. The system will automatically update inventory levels.",
    category: "pharmacy_inventory",
    keywords: ["add", "medicine", "inventory", "stock", "new", "pharmacy"],
    priority: 8
  },
  {
    id: 19,
    question: "How do I dispense medicines to patients?",
    answer: "Go to Pharmacy module, find the medicine request, click **three dots menu**, and select **'View/Edit Details'**. Verify prescription details, check stock availability, dispense required quantities, and update inventory. Generate dispensing receipt for the patient.",
    category: "pharmacy_inventory",
    keywords: ["dispense", "medicine", "patient", "pharmacy", "prescription"],
    priority: 7
  },

  // ============ REPORTS ============
  {
    id: 20,
    question: "How do I generate reports?",
    answer: "Navigate to Reports from the sidebar. Select report type (Patient Reports, Financial Reports, Inventory Reports, etc.). Choose date range, apply filters as needed, and click 'Generate Report'. Reports can be viewed online, downloaded as PDF, or printed directly.",
    category: "reports",
    keywords: ["reports", "generate", "patient", "financial", "inventory"],
    priority: 6
  },
  {
    id: 21,
    question: "How do I create custom reports?",
    answer: "In the Reports module, select 'Custom Reports' option. Choose data sources, select fields to include, apply filters and sorting criteria. Save the report template for future use and generate the report with current data.",
    category: "reports",
    keywords: ["custom", "reports", "create", "template", "data"],
    priority: 5
  },

  // ============ SYSTEM ADMINISTRATION ============
  {
    id: 22,
    question: "How do I backup system data?",
    answer: "Navigate to System Administration (Admin access required). Go to 'Data Management' section, select 'Create Backup'. Choose backup type (Full/Incremental), select data categories, and initiate backup process. Backups are stored securely and can be scheduled for automatic execution.",
    category: "system_administration",
    keywords: ["backup", "data", "system", "administration", "security"],
    priority: 4
  },
  {
    id: 23,
    question: "How do I reset a user's password?",
    answer: "Go to User Management, find the user, click **three dots menu**, and select **'Edit User Details'**. In the user form, you can reset their password. The system will send new login credentials via email to the user automatically.",
    category: "system_administration",
    keywords: ["password", "reset", "user", "login", "credentials"],
    priority: 6
  },
  {
    id: 24,
    question: "How do I manage user permissions?",
    answer: "User permissions are role-based. Go to User Management, click **three dots menu** for the user, select **'Change Role'** to assign different permission levels. Only Admin and Super Admin can modify user roles and permissions.",
    category: "system_administration",
    keywords: ["permissions", "user", "role", "access", "admin"],
    priority: 5
  },

  // ============ EMERGENCY & COMMON ISSUES ============
  {
    id: 25,
    question: "What should I do if the system is running slowly?",
    answer: "Try these troubleshooting steps: 1) Refresh your browser, 2) Clear browser cache, 3) Check internet connection, 4) Close unnecessary browser tabs, 5) Contact IT support if issues persist. For immediate assistance, contact BeenaIT Solutions at contact-us@beenait.com or call +91 77177 32269.",
    category: "technical_support",
    keywords: ["slow", "performance", "system", "troubleshooting", "browser"],
    priority: 6
  },
  {
    id: 26,
    question: "How do I resolve printing issues?",
    answer: "For printing problems: 1) Check printer connection and paper, 2) Verify browser print settings, 3) Try printing from different browser, 4) Check printer drivers are updated, 5) Use 'Print Preview' before printing. Contact BeenaIT Solutions for technical support: contact-us@beenait.com or +91 77177 32269.",
    category: "technical_support",
    keywords: ["printing", "print", "issues", "problems", "troubleshooting"],
    priority: 5
  },

  // ============ IPD & WARD MANAGEMENT ============
  {
    id: 27,
    question: "How do I admit a patient to IPD?",
    answer: "Navigate to Nursing module, find an available bed, click **three dots menu**, and select **'Bed Allocation'**. Choose the patient, select admission type, assign attending doctor, and complete admission formalities. The system will update bed status and notify relevant departments.",
    category: "ipd_ward_management",
    keywords: ["admit", "IPD", "patient", "bed", "allocation", "nursing"],
    priority: 7
  },
  {
    id: 28,
    question: "How do I discharge a patient?",
    answer: "In Nursing module, find the patient's bed, click **three dots menu**, and select **'Initiate/Manage Discharge'**. Complete discharge summary, generate final bill, obtain necessary approvals, and then select **'Confirm Discharge'** to complete the process.",
    category: "ipd_ward_management",
    keywords: ["discharge", "patient", "IPD", "summary", "nursing"],
    priority: 7
  },

  // ============ TECHNICAL SUPPORT ============
  {
    id: 29,
    question: "Who should I contact for technical support?",
    answer: "For all technical issues, system problems, or HMIS support, contact **BeenaIT Solutions**:\n\n📧 **Email:** contact-us@beenait.com\n📞 **Phone:** +91 77177 32269\n\nThey provide comprehensive technical support for the HMIS system including troubleshooting, user training, and system maintenance.",
    category: "technical_support",
    keywords: ["technical", "support", "contact", "help", "BeenaIT", "assistance"],
    priority: 8
  },

  // ============ THREE DOTS MENU OPTIONS (COMPREHENSIVE) ============
  {
    id: 30,
    question: "What are the three dots menu options for patients?",
    answer: "Patient three dots menu options:\n• **Edit Details** - Modify patient information\n• **Add Consultation** - Create new consultation\n• **Add Service** - Add medical services\n• **Add Appointment** - Schedule appointment\n• **Add Reports** - Add medical reports\n• **Add Bed** - Allocate bed for admission\n• **Change Bed** - Transfer to different bed\n• **Buy Medicine** - Purchase medicines\n• **Bill** - Generate patient bill\n• **Delete Patient** - Remove patient (Admin only)",
    category: "navigation_help",
    keywords: ["three dots", "menu", "patient", "options", "actions"],
    priority: 9
  },
  {
    id: 31,
    question: "What are the three dots menu options for appointments?",
    answer: "Appointment three dots menu options:\n• **Add Consultation** - Start consultation from appointment\n• **Reschedule** - Change appointment date/time\n• **Bill** - Generate appointment bill\n• **Cancel** - Cancel the appointment\n• **Delete** - Remove appointment record",
    category: "navigation_help",
    keywords: ["three dots", "menu", "appointment", "options", "actions"],
    priority: 8
  },
  {
    id: 32,
    question: "What are the three dots menu options for users?",
    answer: "User three dots menu options:\n• **Change Role** - Modify user role/permissions\n• **Change Unit** - Transfer to different department\n• **Edit User Details** - Update user information\n• **Delete** - Remove user account (Admin only)",
    category: "navigation_help",
    keywords: ["three dots", "menu", "user", "options", "actions"],
    priority: 7
  },
  {
    id: 33,
    question: "What are the three dots menu options in Nursing/Bed Management?",
    answer: "Nursing/Bed Management three dots menu options:\n• **Patient Details** - View patient information\n• **Request Medicine** - Request medications\n• **Medicine Request Tracking** - Track medicine requests\n• **Return Medicine** - Return unused medicines\n• **Add Surgery/Procedure** - Schedule procedures\n• **Nursing Assessment** - Record nursing observations\n• **Request Investigation** - Order lab tests\n• **Doctors Visits** - Schedule doctor visits\n• **Initiate/Manage Discharge** - Start discharge process\n• **Discharge Summary** - Generate discharge summary\n• **Confirm Discharge** - Complete discharge\n• **Bed History** - View bed allocation history\n• **Bed Transfer** - Transfer to another bed\n• **Bed Dislocation** - Handle bed changes\n• **Unit Transfer** - Transfer between units\n• **Unit Transfer History** - View transfer history\n• **Bill** - Generate patient bill\n• **Note** - Add nursing notes\n• **Bed Under Repair** - Mark bed for maintenance\n• **Ready To Use** - Mark bed as available\n• **Bed Allocation** - Allocate bed to patient",
    category: "navigation_help",
    keywords: ["three dots", "menu", "nursing", "bed", "options", "actions"],
    priority: 8
  },
  {
    id: 34,
    question: "What are the three dots menu options for Store/Inventory?",
    answer: "Store/Inventory three dots menu options:\n• **View Details** - View item details and stock info\n• **Add Stock** - Add new stock quantity\n• **Delete** - Remove item from inventory",
    category: "navigation_help",
    keywords: ["three dots", "menu", "store", "inventory", "options", "actions"],
    priority: 6
  },
  {
    id: 35,
    question: "What are the three dots menu options for Pathology?",
    answer: "Pathology three dots menu options:\n• **Cancel Report** - Cancel pathology report\n• **Delete** - Remove pathology request (with permissions)",
    category: "navigation_help",
    keywords: ["three dots", "menu", "pathology", "options", "actions"],
    priority: 6
  },
  {
    id: 36,
    question: "What are the three dots menu options for Pharmacy?",
    answer: "Pharmacy three dots menu options:\n• **View/Edit Details** - View and modify medicine request details\n• **Approve Medicine Return** - Approve returned medicines\n• **Bill** - Generate pharmacy bill",
    category: "navigation_help",
    keywords: ["three dots", "menu", "pharmacy", "options", "actions"],
    priority: 6
  }
];

export const categories = [
  {
    id: "user_management",
    name: "User Management",
    description: "User creation, role assignment, and staff management"
  },
  {
    id: "patient_management", 
    name: "Patient Management",
    description: "Patient registration, search, and information management"
  },
  {
    id: "appointments",
    name: "Appointments",
    description: "Booking, scheduling, and managing patient appointments"
  },
  {
    id: "consultation",
    name: "Consultation & OPD",
    description: "Patient consultation, diagnosis, and prescription management"
  },
  {
    id: "billing",
    name: "Billing & Payments", 
    description: "Bill generation, payment processing, and financial records"
  },
  {
    id: "inventory",
    name: "Inventory Management",
    description: "Medicine stock, pharmacy, and inventory tracking"
  },
  {
    id: "ipd",
    name: "IPD & Ward Management",
    description: "Inpatient admission, discharge, and ward management"
  },
  {
    id: "reports",
    name: "Reports & Analytics",
    description: "Generating various reports and analytics"
  },
  {
    id: "system",
    name: "System Administration",
    description: "User management, system settings, and technical support"
  }
];
